import type { Group } from './types';

/** When a member's notifications arrive: only while the business is open (the rest wait for opening), or any time. */
export type NotifyMode = 'business' | 'always';
export const NOTIFY_MODE_LABELS: Record<NotifyMode, string> = { business: 'שעות הפעילות', always: 'תמיד' };

/** A notification role: the groups whose new arrivals its members hear about. */
export type NotifyRole = { id: string; name: string; groupIds: string[] };

/** A member with no roles set gets this one. */
export const DEFAULT_ROLE_ID = 'sales';

/** Pro Car's roles, by group name; resolved to the tenant's group ids until an admin saves their own. */
const DEFAULT_ROLES: Array<{ id: string; name: string; groupNames: string[] }> = [
  { id: 'screening', name: 'סינון ראשוני', groupNames: ['לידים חדשים'] },
  { id: 'retry', name: 'הזדמנות שניה', groupNames: ['לנסות שוב'] },
  { id: 'sales', name: 'מכירות', groupNames: ['בדיקת מימון', 'למצוא רכב ספציפי', 'רוצים למכור רכב'] },
];

/** The tenant's roles: its saved ones, or the defaults matched to its groups by name. */
export function resolveNotifyRoles(saved: NotifyRole[] | null | undefined, groups: Group[]): NotifyRole[] {
  if (saved?.length) return saved;
  return DEFAULT_ROLES.map((r) => ({ id: r.id, name: r.name, groupIds: groups.filter((g) => r.groupNames.includes(g.name)).map((g) => g.id) }));
}

export function parseNotifyRoles(v: unknown): NotifyRole[] | null {
  if (!Array.isArray(v)) return null;
  const roles = v.flatMap((r) => {
    const o = r as Partial<NotifyRole>;
    if (typeof o?.id !== 'string' || !o.id || typeof o.name !== 'string') return [];
    return [{ id: o.id, name: o.name, groupIds: Array.isArray(o.groupIds) ? o.groupIds.map(String) : [] }];
  });
  return roles.length ? roles : null;
}

/** A member's role ids (stored as JSON; NULL or unreadable = the default role). */
export function memberRoleIds(stored: string | null | undefined): string[] {
  try {
    const v = JSON.parse(stored ?? 'null');
    if (Array.isArray(v)) return v.map(String);
  } catch { /* fall through */ }
  return [DEFAULT_ROLE_ID];
}

/** The groups a member hears about. */
export function memberGroupIds(roleIds: string[], roles: NotifyRole[]): Set<string> {
  return new Set(roles.filter((r) => roleIds.includes(r.id)).flatMap((r) => r.groupIds));
}

/** A member's own group choice (stored as JSON; NULL or unreadable = not chosen yet). */
export function memberGroupChoice(stored: string | null | undefined): string[] | null {
  try {
    const v = JSON.parse(stored ?? 'null');
    if (Array.isArray(v)) return v.map(String);
  } catch { /* fall through */ }
  return null;
}

/** The groups a member hears about: their own choice, or (until they make one) their roles' groups. */
export function effectiveGroupIds(m: { notify_groups?: string | null; notify_roles?: string | null }, roles: NotifyRole[]): Set<string> {
  return new Set(memberGroupChoice(m.notify_groups) ?? memberGroupIds(memberRoleIds(m.notify_roles), roles));
}
